import { useEffect, useMemo, useRef } from 'react'
import clsx from 'clsx'

import { api } from '@/lib/api'
import { plataCentavos } from '@/lib/formato'
import type { ProductoPos } from './tipos'

interface Props {
  /** El catálogo entero, solo para armar los chips y contar. */
  productos: ProductoPos[]

  /** Lo que se está mostrando, ya filtrado. Lo calcula la pantalla. */
  visibles: ProductoPos[]

  categoria: number | null
  onCategoria: (id: number | null) => void

  onElegir: (p: ProductoPos) => void

  /** Lo que hay tipeado en el buscador de la pantalla. Ver el comentario. */
  filtro: string

  /** Qué producto está marcado con las flechas, o null si ninguno. */
  marcado: number | null

  /**
   * Cuántas columnas entran, para que la flecha de abajo baje una FILA.
   *
   * Lo informa la grilla y no lo calcula la pantalla porque el número lo
   * decide el CSS (`auto-fill` sobre el ancho disponible), y repetir acá la
   * cuenta del ancho mínimo y el espacio entre tarjetas sería una copia que se
   * queda vieja el día que alguien toca la hoja de estilos. Se lee del
   * navegador, que es el que ya lo resolvió.
   */
  onColumnas: (cuantas: number) => void
}

// Grilla de productos del mostrador.
//
// Se filtra EN MEMORIA y no contra el servidor. No es una optimización
// prematura: son unos cientos de productos, y el cajero tipea el código
// mientras el cliente sigue hablando. Un request por tecla agrega latencia
// justo en el momento en que el sistema tiene que desaparecer.
//
// El filtro ignora acentos porque nadie escribe «limón» con tilde tipeando
// rápido, y busca por nombre Y por código: el cajero con práctica usa el
// código, el nuevo busca por nombre.
//
// El texto NO es de esta grilla: viene del único buscador de la pantalla.
// Había dos campos —el del lector arriba y uno propio acá— y eran dos formas
// de hacer lo mismo, una al lado de la otra: el cajero tenía que decidir en
// cuál tipear antes de empezar, y el lector de códigos escribe en el que tenga
// el foco. Uno solo: se escanea, se tipea un PLU o se escribe el nombre, y la
// grilla acompaña.
//
// Y lo que se muestra tampoco lo decide esta grilla: se lo pasa la pantalla ya
// filtrado (`productosVisibles`). Las flechas marcan una POSICIÓN de esa
// lista, así que si cada uno la calculara por su lado el Enter podría agregar
// un producto distinto del que está resaltado.
export function GrillaProductos({
  productos,
  visibles,
  categoria,
  onCategoria,
  onElegir,
  filtro,
  marcado,
  onColumnas,
}: Props) {
  const refGrilla = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const caja = refGrilla.current
    if (!caja) return

    const medir = () => {
      // `gridTemplateColumns` resuelto es la lista de anchos reales
      // («148px 148px 148px»), así que contar las entradas ES la cantidad de
      // columnas, sin volver a derivar nada del ancho.
      const cuantas = getComputedStyle(caja).gridTemplateColumns.split(' ').filter(Boolean).length

      onColumnas(Math.max(1, cuantas))
    }

    medir()

    const observador = new ResizeObserver(medir)
    observador.observe(caja)

    return () => observador.disconnect()
  }, [onColumnas, visibles.length])

  const categorias = useMemo(() => {
    const mapa = new Map<number, { id: number; nombre: string; color: string; cuantos: number }>()

    for (const p of productos) {
      const actual = mapa.get(p.categoriaId)

      if (actual) actual.cuantos++
      else
        mapa.set(p.categoriaId, {
          id: p.categoriaId,
          nombre: p.categoriaNombre,
          color: p.categoriaColor,
          cuantos: 1,
        })
    }

    return [...mapa.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
  }, [productos])

  return (
    <div className="flex min-h-0 flex-col gap-3">
      {/* Categorías. Es la navegación real del mostrador: el cajero conoce el
          rubro y va directo, no busca por nombre. */}
      <div className="flex flex-wrap gap-2">
        <Chip activo={categoria === null || filtro.trim() !== ''} onClick={() => onCategoria(null)}>
          Todo <span className="opacity-50">({productos.length})</span>
        </Chip>

        {categorias.map((c) => (
          <Chip
            key={c.id}
            activo={categoria === c.id && filtro.trim() === ''}
            color={c.color}
            onClick={() => onCategoria(categoria === c.id ? null : c.id)}
          >
            {c.nombre} <span className="opacity-50">({c.cuantos})</span>
          </Chip>
        ))}
      </div>

      {/* Grilla */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {visibles.length === 0 ? (
          <p className="py-16 text-center text-[14px] text-tinta-tenue">
            {filtro.trim()
              ? `No hay productos que coincidan con «${filtro.trim()}».`
              : 'No hay productos en esta categoría.'}
          </p>
        ) : (
          <div
            ref={refGrilla}
            className="grid gap-2.5"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))' }}
          >
            {visibles.map((p, i) => (
              <Tarjeta key={p.id} producto={p} onElegir={onElegir} marcada={i === marcado} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function Tarjeta({
  producto,
  onElegir,
  marcada,
}: {
  producto: ProductoPos
  onElegir: (p: ProductoPos) => void
  marcada: boolean
}) {
  const color = producto.color || producto.categoriaColor || '#8B8579'
  const ref = useRef<HTMLButtonElement>(null)

  // La marcada se trae a la vista, y con `nearest`: la grilla scrollea en su
  // propia caja y centrar haría saltar la pantalla entera en cada flecha. Sin
  // esto, bajar con el teclado llevaba la marca a un producto que estaba
  // abajo del borde, o sea eligiendo a ciegas.
  //
  // El foco NO se mueve: vive en el campo de búsqueda porque el lector de
  // códigos es un teclado, y sacárselo para resaltar una tarjeta dejaría al
  // mostrador sin poder escanear justo mientras se busca.
  useEffect(() => {
    if (marcada) ref.current?.scrollIntoView({ block: 'nearest' })
  }, [marcada])

  // La URL la manda el host y acá solo se le pone el prefijo de la API
  // (`urlImagen`). El `tieneFoto` queda como respaldo para una caja con un
  // ejecutable viejo, que informa que hay foto y todavía no manda la ruta: sin
  // eso, actualizar la nube antes que las cajas dejaría el mostrador sin
  // dibujos hasta que le llegue el OTA.
  const foto = producto.fotoMiniaturaUrl
    ? api.urlImagen(producto.fotoMiniaturaUrl)
    : producto.tieneFoto
      ? api.urlImagen(`/api/productos/${producto.id}/foto`)
      : null

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onElegir(producto)}
      // Alto mínimo de 96 px: es el tamaño con el que un dedo acierta sin
      // mirar, que es como se opera un mostrador con gente esperando.
      className={clsx(
        `sin-seleccion group flex min-h-[96px] flex-col justify-between gap-1 overflow-hidden
         rounded-xl border bg-papel p-2.5 text-left transition
         hover:border-verde hover:shadow-sm active:scale-[0.98]`,
        // La marcada se ve como el chip activo y no como un `hover`: el cajero
        // la está mirando de reojo mientras el cliente habla, así que tiene que
        // distinguirse de lejos y no depender del color del borde.
        marcada ? 'border-verde bg-verde-tenue ring-2 ring-verde' : 'border-borde',
      )}
    >
      <div className="flex items-start gap-2">
        {foto ? (
          <img
            src={foto}
            alt=""
            loading="lazy"
            className="h-10 w-10 shrink-0 rounded-lg object-cover"
          />
        ) : (
          <span
            aria-hidden
            className="mt-0.5 h-10 w-2 shrink-0 rounded-full"
            style={{ background: color }}
          />
        )}

        <span className="line-clamp-2 text-[13.5px] font-semibold leading-tight text-tinta">
          {producto.nombre}
        </span>
      </div>

      <div className="flex items-end justify-between gap-1">
        <span className="num text-[14px] font-semibold text-tinta">
          {plataCentavos(producto.precioCentavos)}
        </span>

        <span className="text-[11.5px] text-tinta-tenue">
          /{producto.unidadAbreviatura}
        </span>
      </div>

      {producto.codigoCorto && (
        <span className="num text-[11px] text-tinta-tenue">{producto.codigoCorto}</span>
      )}
    </button>
  )
}

function Chip({
  activo,
  color,
  onClick,
  children,
}: {
  activo: boolean
  color?: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'sin-seleccion flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition',
        activo
          ? 'border-tinta bg-tinta text-crema'
          : 'border-borde bg-papel text-tinta-suave hover:border-borde-fuerte',
      )}
    >
      {color && (
        <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: color }} />
      )}
      {children}
    </button>
  )
}
